import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { api } from "@/lib/api";
import { useQuery, invalidate } from "@/lib/useQuery";
import type { AdminUser, Inbox, AccountListItem, CatalogItem, GoogleStatus } from "@/lib/types";
import { money, shortDate } from "@/lib/format";
import { Button, Card, PageHeader, Loading, Modal, Field, Toggle, toast, Tag, Empty } from "@/components/ui";
import { useMe } from "@/app/MeContext";

type Section = "users" | "inboxes" | "calendar" | "catalog" | "templates";
const SECTIONS: Section[] = ["users", "inboxes", "calendar", "catalog", "templates"];

export function AdminPage() {
  const me = useMe();
  const [params, setParams] = useSearchParams();
  const fromUrl = params.get("section") as Section | null;
  const [section, setSection] = useState<Section>(fromUrl && SECTIONS.includes(fromUrl) ? fromUrl : "users");
  useEffect(() => {
    // Google's OAuth callback lands on /admin?section=calendar&google=connected|error&msg=...
    const g = params.get("google");
    if (g) { toast(params.get("msg") ?? (g === "connected" ? "Google connected" : "Google sign-in failed"), g !== "connected"); setParams({ section: "calendar" }, { replace: true }); }
  }, [params, setParams]);
  if (me.role !== "admin") return <div className="page"><Card><div className="empty">Admin only.</div></Card></div>;
  return (
    <>
      <PageHeader title="Admin">
        <div className="seg">
          {SECTIONS.map((s) => <button key={s} className={section === s ? "active" : ""} onClick={() => setSection(s)}>{s[0].toUpperCase() + s.slice(1)}</button>)}
        </div>
      </PageHeader>
      <div className="page">
        {section === "users" && <Users />}
        {section === "inboxes" && <Inboxes />}
        {section === "calendar" && <GoogleCalendarCard />}
        {section === "catalog" && <Catalog />}
        {section === "templates" && <Templates />}
      </div>
    </>
  );
}

function GoogleCalendarCard() {
  const { data: g, loading, refetch } = useQuery<GoogleStatus>("admin-google", () => api.get<GoogleStatus>("/admin/google"));
  const adminConnected = !!g?.connected && g.source === "admin";
  const { data: cals, loading: calsLoading, refetch: refetchCals } = useQuery<{ id: string; name: string; primary: boolean }[]>(`admin-google-cals:${adminConnected}`, () => adminConnected ? api.get("/admin/google/calendars") : Promise.resolve([]), [adminConnected]);
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    setBusy(true);
    try { const r = await api.post<{ url: string }>("/admin/google/start", {}); window.location.href = r.url; } catch (e) { toast((e as Error).message, true); setBusy(false); }
  };
  const pick = async (id: string) => {
    const name = (cals ?? []).find((c) => c.id === id)?.name ?? "";
    try { await api.patch("/admin/google", { calendarId: id, calendarName: name }); toast(`Using "${name || id}"`); refetch(); invalidate("cal"); } catch (e) { toast((e as Error).message, true); }
  };
  const disconnect = async () => {
    if (!confirm("Disconnect Google? Invites will stop syncing to Google Calendar until you connect again.")) return;
    try { await api.del("/admin/google"); toast("Google disconnected"); refetch(); } catch (e) { toast((e as Error).message, true); }
  };
  return (
    <Card title="Google Calendar" actions={adminConnected ? <Button size="md" variant="danger" onClick={disconnect}>Disconnect</Button> : g?.configured ? <Button size="md" variant="primary" disabled={busy} onClick={connect}>{busy ? "Redirecting…" : "Connect Google"}</Button> : null}>
      {loading && !g ? <Loading /> : !g ? null : (
        <div className="card-body col gap-12">
          <div className="small muted-2">Teams invites that arrive by email are added to this calendar, and the Calendar page shows what is on it. Only the connected Google account's events are read; nothing is written except the invites OpsDesk creates.</div>
          {!g.configured && (
            <div className="col gap-6 small">
              <div><Tag color="yellow">Not configured</Tag> <span className="muted-2">Add the two GitHub Actions secrets <span className="mono">GOOGLE_CLIENT_ID</span> and <span className="mono">GOOGLE_CLIENT_SECRET</span> from a Google Cloud OAuth client (type Web application), then redeploy. The README walks through it.</span></div>
              <div className="muted-2">Authorized redirect URI to register: <span className="mono" style={{ userSelect: "all" }}>{g.redirectUri}</span></div>
            </div>
          )}
          {g.configured && !g.connected && <div className="small muted-2">Not connected. Click Connect Google, sign in with the Gmail account whose calendar you want, and allow calendar access. Redirect URI registered on the OAuth client must be <span className="mono" style={{ userSelect: "all" }}>{g.redirectUri}</span>.</div>}
          {g.connected && g.source === "secrets" && <div className="small"><Tag color="green">Connected</Tag> <span className="muted-2">using the <span className="mono">GOOGLE_REFRESH_TOKEN</span> secret, calendar <span className="mono">{g.calendarId}</span>. Connect Google here to switch to a signed-in account and pick a calendar.</span>{g.configured && <div className="mt-8"><Button size="md" variant="primary" disabled={busy} onClick={connect}>{busy ? "Redirecting…" : "Connect Google"}</Button></div>}</div>}
          {adminConnected && (
            <>
              <dl className="kv">
                <dt>Account</dt><dd>{g.email || <span className="muted">unknown</span>} <Tag color="green">Connected</Tag></dd>
                <dt>Since</dt><dd className="muted-2">{shortDate(g.connectedAt)}</dd>
                <dt>Calendar</dt>
                <dd>
                  {calsLoading && !cals ? <span className="muted">Loading calendars…</span> : (
                    <select className="select" style={{ maxWidth: 360 }} value={(g.calendarId === "primary" ? (cals ?? []).find((c) => c.primary)?.id : null) ?? g.calendarId ?? "primary"} onChange={(e) => pick(e.target.value)}>
                      {g.calendarId !== "primary" && !(cals ?? []).some((c) => c.id === g.calendarId) && <option value={g.calendarId ?? ""}>{g.calendarName || g.calendarId}</option>}
                      {g.calendarId === "primary" && !(cals ?? []).some((c) => c.primary) && <option value="primary">Primary calendar</option>}
                      {(cals ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}{c.primary ? " (primary)" : ""}</option>)}
                    </select>
                  )}
                  <button className="btn-link small muted" style={{ marginLeft: 8 }} onClick={() => refetchCals()}>Refresh list</button>
                </dd>
              </dl>
              <div className="xs muted">Only calendars you can write to are listed. Invites go to the selected calendar; the Calendar page reads from it.</div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

function Users() {
  const { data: users, loading, refetch } = useQuery<AdminUser[]>("admin-users", () => api.get<AdminUser[]>("/admin/users"));
  const { data: accounts } = useQuery<AccountListItem[]>("accounts", () => api.get<AccountListItem[]>("/accounts"));
  const [editing, setEditing] = useState<AdminUser | "new" | null>(null);
  return (
    <Card title="Users" actions={<Button size="md" variant="primary" onClick={() => setEditing("new")}>Add user</Button>}>
      {loading && !users ? <Loading /> : (
        <table className="table">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Permissions</th><th>Accounts</th><th>Last seen</th><th></th></tr></thead>
          <tbody>
            {(users ?? []).map((u) => (
              <tr key={u.id}>
                <td style={{ fontWeight: 500 }}>{u.name}{!u.active && <Tag color="outline">Inactive</Tag>}</td>
                <td className="muted-2">{u.email}</td>
                <td><Tag color={u.role === "admin" ? "accent" : "neutral"}>{u.role}</Tag></td>
                <td className="muted-2 small">{u.role === "admin" ? "All" : [u.permissions.seePricing && "pricing", u.permissions.sendQuotes && "quotes", u.permissions.seeInvoices && "invoices", u.permissions.editStandardDocs && "docs"].filter(Boolean).join(", ") || "—"}</td>
                <td className="muted-2 small">{u.role === "admin" ? "All" : `${u.accountIds.length}`}</td>
                <td className="muted small">{u.lastSeenAt ? shortDate(u.lastSeenAt) : "Never"}</td>
                <td className="right"><button className="btn-link small" onClick={() => setEditing(u)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing && <UserModal user={editing === "new" ? null : editing} accounts={accounts ?? []} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refetch(); invalidate("accounts"); }} />}
    </Card>
  );
}

function UserModal({ user, accounts, onClose, onSaved }: { user: AdminUser | null; accounts: AccountListItem[]; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ name: user?.name ?? "", email: user?.email ?? "", username: user?.username ?? "", password: "", role: user?.role ?? "member", active: user?.active ?? true, permissions: { ...(user?.permissions ?? { seePricing: false, sendQuotes: false, seeInvoices: false, editStandardDocs: false }) }, accountIds: new Set(user?.accountIds ?? []) });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const body = { name: f.name, role: f.role, active: f.active, permissions: f.permissions, accountIds: [...f.accountIds], username: f.username || null, ...(f.password ? { password: f.password } : {}) };
      if (user) await api.patch(`/admin/users/${user.id}`, body);
      else await api.post("/admin/users", { ...body, email: f.email });
      onSaved();
    } catch (e) { toast((e as Error).message, true); } finally { setBusy(false); }
  };
  const perm = (k: keyof typeof f.permissions, label: string) => (
    <div className="row gap-10" style={{ minHeight: 32 }}><Toggle on={f.permissions[k]} onChange={(v) => setF((x) => ({ ...x, permissions: { ...x.permissions, [k]: v } }))} label={label} /><span>{label}</span></div>
  );
  return (
    <Modal title={user ? "Edit user" : "Add user"} onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={busy || !f.name || !f.email} onClick={save}>Save</Button></>}>
      <div className="form-grid">
        <Field label="Name"><input className="input" value={f.name} onChange={(e) => setF((x) => ({ ...x, name: e.target.value }))} /></Field>
        <Field label="Google email" hint="Must match the Cloudflare Access login"><input className="input" disabled={!!user} value={f.email} onChange={(e) => setF((x) => ({ ...x, email: e.target.value }))} /></Field>
        <Field label="Username" hint="For password sign-in while Access is off"><input className="input" autoComplete="off" value={f.username} onChange={(e) => setF((x) => ({ ...x, username: e.target.value }))} /></Field>
        <Field label={user?.hasPassword ? "New password (blank keeps current)" : "Password"} hint="At least 8 characters"><input className="input" type="password" autoComplete="new-password" value={f.password} onChange={(e) => setF((x) => ({ ...x, password: e.target.value }))} /></Field>
        <Field label="Role"><select className="select" value={f.role} onChange={(e) => setF((x) => ({ ...x, role: e.target.value as "admin" | "member" }))}><option value="member">Member</option><option value="admin">Admin</option></select></Field>
        <Field label="Active"><div className="row" style={{ height: 32 }}><Toggle on={f.active} onChange={(v) => setF((x) => ({ ...x, active: v }))} /></div></Field>
      </div>
      {f.role === "member" && (
        <>
          <div className="label mt-8">Permissions</div>
          {perm("seePricing", "See pricing")}
          {perm("sendQuotes", "Send quotes")}
          {perm("seeInvoices", "See invoices")}
          {perm("editStandardDocs", "Edit standard docs")}
          <div className="label mt-8">Assigned accounts</div>
          <div className="col gap-4" style={{ maxHeight: 220, overflowY: "auto" }}>
            {accounts.map((a) => (
              <label key={a.id} className="row gap-8" style={{ minHeight: 28 }}>
                <input type="checkbox" checked={f.accountIds.has(a.id)} onChange={(e) => setF((x) => { const s = new Set(x.accountIds); e.target.checked ? s.add(a.id) : s.delete(a.id); return { ...x, accountIds: s }; })} />
                {a.name} <span className="mono xs muted">{a.number}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </Modal>
  );
}

function Inboxes() {
  const { data, loading, refetch } = useQuery<Inbox[]>("admin-inboxes", () => api.get<Inbox[]>("/admin/inboxes"));
  const { data: users } = useQuery<AdminUser[]>("admin-users", () => api.get<AdminUser[]>("/admin/users"));
  const [editing, setEditing] = useState<Inbox | "new" | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [days, setDays] = useState("14");
  const { data: failed, refetch: refetchFailed } = useQuery<{ failed: number }>("ai-failed", () => api.get<{ failed: number }>("/ai/reprocess-failed"));
  const [rerunning, setRerunning] = useState(false);
  const rerun = async () => {
    setRerunning(true);
    try {
      const r = await api.post<{ queued: number }>("/ai/reprocess-failed", {});
      toast(r.queued ? `${r.queued} emails sent back to Claude. Results land in the inbox over the next minute or two.` : "Nothing to re-run.");
      refetchFailed();
    } catch (e) { toast((e as Error).message, true); } finally { setRerunning(false); }
  };
  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post<{ scanned: number; imported: number; queued: number; junk: number; skipped: number; errors: string[] }>("/inbox/sync", { classifyDays: Number(days) });
      toast(`Resend sync: ${r.imported} imported (${r.queued} sent to Claude, ${r.junk} junk), ${r.skipped} already here${r.errors.length ? `, ${r.errors.length} errors` : ""}`);
      if (r.errors.length) console.warn(r.errors);
      invalidate("threads"); invalidate("badges");
    } catch (e) { toast((e as Error).message, true); } finally { setSyncing(false); }
  };
  return (
    <>
    <Card title="Inboxes" actions={<>
      <span className="small muted">Run Claude on mail from the last</span>
      <select className="select" style={{ width: 90, height: 28 }} value={days} onChange={(e) => setDays(e.target.value)}><option value="0">none</option><option value="7">7 days</option><option value="14">14 days</option><option value="30">30 days</option><option value="90">90 days</option></select>
      <Button size="md" disabled={syncing} onClick={sync}>{syncing ? "Syncing…" : "Sync from Resend"}</Button>
      {!!failed?.failed && <Button size="md" disabled={rerunning} onClick={rerun} title="Emails whose last Claude run failed (API error or bad output)">{rerunning ? "Queuing…" : `Re-run Claude on ${failed.failed} failed`}</Button>}
      <Button size="md" variant="primary" onClick={() => setEditing("new")}>Add inbox</Button>
    </>}>
      <div className="card-body small muted-2">Each address must be a receiving address on the Resend domain. Shared inboxes let the listed members see every thread; personal inboxes are visible to their owners and admins.</div>
      {loading && !data ? <Loading /> : (
        <table className="table">
          <thead><tr><th>Address</th><th>Name</th><th>Type</th><th>Owners</th><th></th></tr></thead>
          <tbody>
            {(data ?? []).map((ib) => (
              <tr key={ib.id}>
                <td className="mono">{ib.address}{!ib.active && <Tag color="outline">Inactive</Tag>}</td>
                <td>{ib.name}</td>
                <td><Tag>{ib.type}</Tag></td>
                <td className="muted-2 small">{ib.ownerUserIds.map((id) => users?.find((u) => u.id === id)?.name ?? id).join(", ") || "Admins only"}</td>
                <td className="right"><button className="btn-link small" onClick={() => setEditing(ib)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing && <InboxModal inbox={editing === "new" ? null : editing} users={users ?? []} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refetch(); invalidate("badges"); }} />}
    </Card>
    <LeadSources />
      <BlockedSenders />
    </>
  );
}

function LeadSources() {
  const { data, refetch } = useQuery<{ sources: string[] }>("lead-sources", () => api.get("/leads/sources"));
  const [v, setV] = useState("");
  const save = async (sources: string[]) => { try { await api.put("/leads/sources", { sources }); setV(""); refetch(); } catch (e) { toast((e as Error).message, true); } };
  const list = data?.sources ?? [];
  return (
    <Card title="Lead sources" className="mt-16" actions={<><input className="input" style={{ width: 260, height: 28 }} placeholder="forms@hixonops.com or formsite.com" value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && v.trim() && save([...list, v.trim()])} /><Button size="md" disabled={!v.trim()} onClick={() => save([...list, v.trim()])}>Add</Button></>}>
      <div className="card-body small muted-2">Mail from these senders is treated as a website form fill. Claude reads the prospect's details into a lead for you to accept or reject under Leads; the normal classifier does not run on it. Use the exact address your form tool sends from, or its domain.</div>
      {list.length === 0 ? <Empty>No lead sources yet. Add the address your contact form notifications come from.</Empty> : (
        <table className="table"><thead><tr><th>Sender</th><th>Matches</th><th></th></tr></thead>
          <tbody>{list.map((s) => <tr key={s}><td className="mono">{s}</td><td className="muted-2 small">{s.includes("@") ? "exact address" : "any address at this domain"}</td><td className="right"><button className="btn-link small" onClick={() => save(list.filter((x) => x !== s))}>Remove</button></td></tr>)}</tbody></table>
      )}
    </Card>
  );
}

function BlockedSenders() {
  const { data, refetch } = useQuery<{ id: string; pattern: string; kind: string; reason: string | null; createdAt: string }[]>("blocked", () => api.get("/inbox/blocked"));
  const [pattern, setPattern] = useState("");
  return (
    <Card title="Blocked senders" className="mt-16" actions={<><input className="input" style={{ width: 220, height: 28 }} placeholder="name@vendor.com or vendor.com" value={pattern} onChange={(e) => setPattern(e.target.value)} /><Button size="md" disabled={!pattern.trim()} onClick={async () => { await api.post("/inbox/blocked", { pattern: pattern.trim() }); setPattern(""); refetch(); }}>Block</Button></>}>
      <div className="card-body small muted-2">Mail from these addresses or domains is stored under the Junk filter, never classified, and never shows in the main inbox. Open a junk thread and click Not junk to unblock.</div>
      {(data ?? []).length === 0 ? <div className="empty">Nothing blocked.</div> : (
        <table className="table"><thead><tr><th>Pattern</th><th>Kind</th><th>Reason</th><th>Added</th><th></th></tr></thead>
          <tbody>{(data ?? []).map((b) => <tr key={b.id}><td className="mono">{b.pattern}</td><td><Tag>{b.kind}</Tag></td><td className="muted-2 small">{b.reason}</td><td className="muted small">{shortDate(b.createdAt)}</td><td className="right"><button className="btn-link small" onClick={async () => { await api.del(`/inbox/blocked/${b.id}`); refetch(); }}>Unblock</button></td></tr>)}</tbody></table>
      )}
    </Card>
  );
}

function InboxModal({ inbox, users, onClose, onSaved }: { inbox: Inbox | null; users: AdminUser[]; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ address: inbox?.address ?? "", name: inbox?.name ?? "", type: inbox?.type ?? "personal", active: inbox?.active ?? true, owners: new Set(inbox?.ownerUserIds ?? []) });
  const save = async () => {
    try {
      const body = { name: f.name, type: f.type, active: f.active, ownerUserIds: [...f.owners] };
      if (inbox) await api.patch(`/admin/inboxes/${inbox.id}`, body); else await api.post("/admin/inboxes", { ...body, address: f.address });
      onSaved();
    } catch (e) { toast((e as Error).message, true); }
  };
  return (
    <Modal title={inbox ? "Edit inbox" : "Add inbox"} onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!f.address || !f.name} onClick={save}>Save</Button></>}>
      <div className="form-grid">
        <Field label="Address"><input className="input" disabled={!!inbox} placeholder="support@hixonops.com" value={f.address} onChange={(e) => setF((x) => ({ ...x, address: e.target.value }))} /></Field>
        <Field label="Name"><input className="input" placeholder="Support" value={f.name} onChange={(e) => setF((x) => ({ ...x, name: e.target.value }))} /></Field>
        <Field label="Type"><select className="select" value={f.type} onChange={(e) => setF((x) => ({ ...x, type: e.target.value as "personal" | "shared" }))}><option value="personal">Personal</option><option value="shared">Shared</option></select></Field>
        <Field label="Active"><div className="row" style={{ height: 32 }}><Toggle on={f.active} onChange={(v) => setF((x) => ({ ...x, active: v }))} /></div></Field>
      </div>
      <div className="label">Owners</div>
      {users.map((u) => (
        <label key={u.id} className="row gap-8"><input type="checkbox" checked={f.owners.has(u.id)} onChange={(e) => setF((x) => { const s = new Set(x.owners); e.target.checked ? s.add(u.id) : s.delete(u.id); return { ...x, owners: s }; })} />{u.name}</label>
      ))}
    </Modal>
  );
}

function Catalog() {
  const { data, loading, refetch } = useQuery<CatalogItem[]>("catalog", () => api.get<CatalogItem[]>("/catalog"));
  const [editing, setEditing] = useState<CatalogItem | "new" | null>(null);
  return (
    <Card title="Catalog" actions={<Button size="md" variant="primary" onClick={() => setEditing("new")}>Add item</Button>}>
      {loading && !data ? <Loading /> : (data ?? []).length === 0 ? <div className="empty">No catalog items yet.</div> : (
        <table className="table">
          <thead><tr><th>Name</th><th>Kind</th><th>Price</th><th>Add-on</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {(data ?? []).map((it) => (
              <tr key={it.id}>
                <td style={{ fontWeight: 500 }}>{it.name}<div className="xs muted">{it.description}</div></td>
                <td><Tag>{it.kind === "one_time" ? "One-time" : "Monthly"}</Tag></td>
                <td className="num">{money(it.price)}{it.kind === "recurring" && "/mo"}</td>
                <td className="muted-2">{it.isAddOn ? "Yes" : ""}</td>
                <td className="muted-2">{it.active ? "Yes" : "No"}</td>
                <td className="right"><button className="btn-link small" onClick={() => setEditing(it)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing && <CatalogModal item={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refetch(); }} />}
    </Card>
  );
}

function CatalogModal({ item, onClose, onSaved }: { item: CatalogItem | null; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ name: item?.name ?? "", description: item?.description ?? "", kind: item?.kind ?? "one_time", price: item?.price ?? 0, isAddOn: item?.isAddOn ?? false, active: item?.active ?? true });
  const save = async () => {
    try { if (item) await api.patch(`/catalog/${item.id}`, f); else await api.post("/catalog", f); onSaved(); } catch (e) { toast((e as Error).message, true); }
  };
  return (
    <Modal title={item ? "Edit item" : "Add catalog item"} onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!f.name} onClick={save}>Save</Button></>}>
      <Field label="Name"><input className="input" value={f.name} onChange={(e) => setF((x) => ({ ...x, name: e.target.value }))} /></Field>
      <Field label="Description"><input className="input" value={f.description} onChange={(e) => setF((x) => ({ ...x, description: e.target.value }))} /></Field>
      <div className="form-grid">
        <Field label="Kind"><select className="select" value={f.kind} onChange={(e) => setF((x) => ({ ...x, kind: e.target.value as "one_time" | "recurring" }))}><option value="one_time">One-time</option><option value="recurring">Recurring (monthly)</option></select></Field>
        <Field label="Price"><input className="input" type="number" step="0.01" value={f.price} onChange={(e) => setF((x) => ({ ...x, price: Number(e.target.value) }))} /></Field>
      </div>
      <label className="row gap-8"><input type="checkbox" checked={f.isAddOn} onChange={(e) => setF((x) => ({ ...x, isAddOn: e.target.checked }))} /> Add-on (attaches to any package)</label>
      <label className="row gap-8"><input type="checkbox" checked={f.active} onChange={(e) => setF((x) => ({ ...x, active: e.target.checked }))} /> Active</label>
    </Modal>
  );
}

interface Template { id: string; name: string; subject: string; body: string; category: string | null }
function Templates() {
  const { data, loading, refetch } = useQuery<Template[]>("templates", () => api.get<Template[]>("/admin/templates"));
  const [editing, setEditing] = useState<Template | "new" | null>(null);
  return (
    <Card title="Email templates" actions={<Button size="md" variant="primary" onClick={() => setEditing("new")}>Add template</Button>}>
      <div className="card-body small muted-2">Placeholders: {"{{contact.first}}"}, {"{{contact.name}}"}, {"{{account.name}}"}, {"{{me.name}}"}.</div>
      {loading && !data ? <Loading /> : (data ?? []).length === 0 ? <div className="empty">No templates yet.</div> : (
        <table className="table">
          <thead><tr><th>Name</th><th>Subject</th><th>Category</th><th></th></tr></thead>
          <tbody>{(data ?? []).map((t) => <tr key={t.id}><td style={{ fontWeight: 500 }}>{t.name}</td><td className="muted-2">{t.subject}</td><td className="muted-2">{t.category}</td><td className="right"><button className="btn-link small" onClick={() => setEditing(t)}>Edit</button></td></tr>)}</tbody>
        </table>
      )}
      {editing && <TemplateModal t={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refetch(); invalidate("templates"); }} />}
    </Card>
  );
}
function TemplateModal({ t, onClose, onSaved }: { t: Template | null; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ name: t?.name ?? "", subject: t?.subject ?? "", body: t?.body ?? "", category: t?.category ?? "" });
  const save = async () => { try { if (t) await api.patch(`/admin/templates/${t.id}`, f); else await api.post("/admin/templates", f); onSaved(); } catch (e) { toast((e as Error).message, true); } };
  return (
    <Modal title={t ? "Edit template" : "Add template"} onClose={onClose} wide footer={<>{t && <Button variant="danger" className="ml-0" onClick={async () => { if (confirm("Delete template?")) { await api.del(`/admin/templates/${t.id}`); onSaved(); } }}>Delete</Button>}<Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!f.name || !f.body} onClick={save}>Save</Button></>}>
      <div className="form-grid">
        <Field label="Name"><input className="input" value={f.name} onChange={(e) => setF((x) => ({ ...x, name: e.target.value }))} /></Field>
        <Field label="Category"><input className="input" placeholder="Follow-up" value={f.category} onChange={(e) => setF((x) => ({ ...x, category: e.target.value }))} /></Field>
      </div>
      <Field label="Subject"><input className="input" value={f.subject} onChange={(e) => setF((x) => ({ ...x, subject: e.target.value }))} /></Field>
      <Field label="Body"><textarea className="textarea" style={{ minHeight: 200 }} value={f.body} onChange={(e) => setF((x) => ({ ...x, body: e.target.value }))} /></Field>
    </Modal>
  );
}
